import { Badge } from '../../ui/badge';
import { KonumPaylas } from '../../maps/KonumPaylas';
import { formatIhaleField, kontrolListesi, varYok } from './format';
import { computeNakdiIlerleme, getSonGuncelSozlesme, toplamIsArtisOrani } from './calc';

export function Section({ no, title, children, testId }) {
  return (
    <section className="pt-4 border-t border-gray-700 first:pt-0 first:border-0" data-testid={testId}>
      <h3 className="text-white font-medium mb-3 flex items-center gap-2">
        <span className="w-5 h-5 rounded-full bg-blue-600/30 text-blue-300 text-[11px] flex items-center justify-center">{no}</span>
        {title}
      </h3>
      {children}
    </section>
  );
}

function Field({ label, value, testId, className = '' }) {
  return (
    <div className={className} data-testid={testId}>
      <span className="text-gray-500">{label}:</span> <span className="text-white ml-2">{value || '-'}</span>
    </div>
  );
}

// Alan adına göre biçimlenmiş değer (kodlu değer → etiket, tarih, VAR/YOK, para)
function F({ ihale, col, label, users, displayMoney, testId }) {
  return <Field label={label} value={formatIhaleField(col, ihale[col], { users, displayMoney })} testId={testId || `ihale-detail-${col.replace(/_/g, '-')}`} />;
}

function Money({ label, value, testId, tone = '' }) {
  return (
    <div className={`flex justify-between bg-gray-800/50 p-2 rounded ${tone}`} data-testid={testId}>
      <span className="text-gray-400">{label}:</span> <span className="text-white font-medium">{value || '-'}</span>
    </div>
  );
}

const grid = 'grid grid-cols-2 md:grid-cols-3 gap-3 text-sm';

export function TemelBilgiler({ ihale, users, displayMoney }) {
  const p = { ihale, users, displayMoney };
  return (
    <Section no="1" title="Temel Bilgiler" testId="ihale-detail-section-1">
      <div className={grid}>
        <F {...p} col="yil" label="Yıl" />
        <F {...p} col="ihale_no" label="İhale No" />
        <F {...p} col="kayit_no" label="Kayıt No" />
        <F {...p} col="ihale_tarihi" label="İhale Tarihi" />
        <F {...p} col="ihale_usulu" label="İhale Usulü" />
        <F {...p} col="alim_turu" label="Alım Türü" />
        <F {...p} col="tesis_turu" label="Tesis Türü" />
        <F {...p} col="sozlesme_turu" label="Sözleşme Türü" />
        <F {...p} col="sozlesme_tarihi" label="Sözleşme Tarihi" />
      </div>
    </Section>
  );
}

export function SorumlularLokasyon({ ihale, users, displayMoney, konum }) {
  const p = { ihale, users, displayMoney };
  return (
    <Section no="2" title="Sorumlular ve Lokasyon" testId="ihale-detail-section-2">
      <div className={grid}>
        <F {...p} col="ilgilenen_memur" label="İlgilenen Memur" />
        <F {...p} col="ilcesi" label="İlçe" />
        <div data-testid="ihale-detail-konum"><span className="text-gray-500">Konum:</span> {ihale.enlem && ihale.boylam ? (
          <a href={`https://www.google.com/maps?q=${ihale.enlem},${ihale.boylam}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 ml-2 underline" data-testid="ihale-detail-konum-link">
            {ihale.enlem}, {ihale.boylam} ↗
          </a>
        ) : <span className="text-white ml-2">-</span>}</div>
        <F {...p} col="isi_yapan_idare" label="İşi Yapan İdare" />
        <F {...p} col="odenek_durumu" label="Ödenek Durumu" />
        <F {...p} col="odenek_tarihi" label="Gelen Ödenek Tarihi" />
      </div>
      {konum && (
        <div className="pt-3 mt-3 border-t border-gray-800">
          <KonumPaylas compact testIdPrefix="ihale-detail"
            item={{ lat: konum.lat, lon: konum.lon, approx: konum.approx, name: `${ihale.isin_adi || ''}${ihale.ihale_no ? ` (${ihale.ihale_no})` : ''}`, district: ihale.ilcesi }} />
        </div>
      )}
    </Section>
  );
}

export function FirmaDurum({ ihale, users, displayMoney }) {
  const p = { ihale, users, displayMoney };
  return (
    <Section no="3" title="Firma ve Durum" testId="ihale-detail-section-3">
      <div className={grid}>
        <div data-testid="ihale-detail-durumu"><span className="text-gray-500">Durum:</span> <Badge className="ml-2 bg-blue-500/20 text-blue-400 border-blue-500/50">{formatIhaleField('durumu', ihale.durumu) || '-'}</Badge></div>
        <F {...p} col="ihaleyi_alan_firma" label="İhaleyi Alan Firma" />
        <F {...p} col="alt_yuklenici_firma" label="Alt Yüklenici Firma" />
        <F {...p} col="firma_iletisim_adresi" label="Firma İletişim Adresi" className="col-span-2 md:col-span-3" />
      </div>
    </Section>
  );
}

function IsArtisiBlok({ ihale, i, displayMoney }) {
  const durum = ihale[`is_artisi_${i}_var_mi`];
  const tutar = ihale[`is_artis_tutari_${i}`];
  const aktif = durum === 'var' || !!tutar;
  return (
    <div className={`border rounded-lg p-3 space-y-2 ${aktif ? 'border-red-500/60' : 'border-gray-700'}`} data-testid={`ihale-detail-is-artisi-${i}`}>
      <div className="flex justify-between text-sm">
        <span className="text-red-400 font-medium">{i}. İş Artışı/Azalışı</span>
        <span className="text-white">{aktif ? 'VAR' : (durum === 'yok' ? 'YOK' : '-')}</span>
      </div>
      {aktif && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
          <Money label="Tutar" value={displayMoney(tutar)} testId={`ihale-detail-is-artis-tutari-${i}`} />
          <Money label="Oran" value={ihale[`is_artis_orani_${i}`] ? `${ihale[`is_artis_orani_${i}`]} %` : ''} testId={`ihale-detail-is-artis-orani-${i}`} />
          <Money label={`${i}. Güncel Sözleşme${i === 3 ? ' (FINAL)' : ''}`} value={displayMoney(ihale[`guncel_sozlesme_bedeli_${i}`])} testId={`ihale-detail-guncel-sozlesme-${i}`} />
        </div>
      )}
    </div>
  );
}

export function FinansalBilgiler({ ihale, displayMoney }) {
  const sonGuncel = getSonGuncelSozlesme(ihale);
  const toplamOran = toplamIsArtisOrani(ihale);
  const nakdi = computeNakdiIlerleme(ihale) || ihale.nakdi_ilerleme || '';
  const sureUzatimi = ihale.is_artisi_var_mi;
  return (
    <Section no="4" title="Finansal Bilgiler" testId="ihale-detail-section-4">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Money label="Yaklaşık Maliyet" value={displayMoney(ihale.yaklasik_maliyet)} testId="ihale-detail-yaklasik-maliyet" />
          <Money label="Sözleşme Bedeli" value={displayMoney(ihale.sozlesme_bedeli)} testId="ihale-detail-sozlesme-bedeli" />
        </div>
        {[1, 2, 3].map(i => <IsArtisiBlok key={i} ihale={ihale} i={i} displayMoney={displayMoney} />)}
        <div className="bg-blue-900/20 border border-blue-500 rounded-lg p-3">
          <h4 className="text-blue-400 font-medium text-sm mb-2">Toplam Özet</h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Money label="Toplam İş Artış/Azalış Oranı" value={toplamOran ? `${toplamOran} %` : ''} testId="ihale-detail-toplam-oran" tone="bg-blue-900/30" />
            <Money label="Toplam Sözleşme Bedeli (Son Güncel)" value={sonGuncel > 0 ? displayMoney(sonGuncel.toFixed(2).replace('.', ',')) : ''} testId="ihale-detail-son-guncel" tone="bg-blue-900/30" />
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          <Money label="Toplam Ödenen Hakediş (KDV Dahil)" value={displayMoney(ihale.toplam_odenen_hakedis)} testId="ihale-detail-hakedis" />
          <Money label="Nakdi İlerleme" value={nakdi ? `${nakdi} %` : ''} testId="ihale-detail-nakdi-ilerleme" />
          <Money label="Fiziki İlerleme" value={ihale.fiziki_ilerleme ? `${ihale.fiziki_ilerleme} %` : ''} testId="ihale-detail-fiziki-ilerleme" />
          <Money label="Fiyat Farkı" value={varYok(ihale.fiyat_farki)} testId="ihale-detail-fiyat-farki" />
          <Money label="Fiyat Farkı Tutarı" value={displayMoney(ihale.fiyat_farki_tutari)} testId="ihale-detail-fiyat-farki-tutari" />
        </div>
        <div className="p-3 bg-yellow-900/20 border border-yellow-700/50 rounded-lg space-y-2 text-sm" data-testid="ihale-detail-sure-uzatimi-blok">
          <div className="flex justify-between"><span className="text-yellow-300">İş Artışı/Süre Uzatımı Var mı?</span><span className="text-white">{varYok(sureUzatimi) || '-'}</span></div>
          {sureUzatimi === 'var' && (
            <div className="grid grid-cols-3 gap-2 pt-2 border-t border-yellow-700/30">
              <Field label="Ek Kesin Teminat" value={varYok(ihale.ek_kesin_teminat)} testId="ihale-detail-ek-kesin-teminat" />
              <Field label="Revize İş Programı" value={varYok(ihale.revize_is_programi)} testId="ihale-detail-revize-is-programi" />
              <Field label="İlave All Risk" value={varYok(ihale.ilave_all_risk)} testId="ihale-detail-ilave-all-risk" />
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}

export function SureKabul({ ihale, users, displayMoney }) {
  const p = { ihale, users, displayMoney };
  const kontrol = kontrolListesi(ihale.kontrol_teskilati, users);
  return (
    <Section no="5" title="Süre, Kabul ve Kontrol Teşkilatı" testId="ihale-detail-section-5">
      <div className={grid}>
        <F {...p} col="yer_teslim_tarihi" label="Yer Teslim Tarihi" />
        <F {...p} col="isin_suresi" label="İşin Süresi (Gün)" />
        <F {...p} col="bitis_tarihi" label="Bitiş Tarihi" />
        <F {...p} col="ilave_sure" label="Süre Uzatımı (Gün)" />
        <F {...p} col="sure_uzatimi" label="İlave Süreli İş Bitim Tarihi" />
        <F {...p} col="gecici_kabul_tarihi" label="Geçici Kabul Tarihi" />
        <F {...p} col="teminat_suresi_ay" label="Teminat Süresi (Ay)" />
        <F {...p} col="kesin_kabul_tarihi" label="Kesin Kabul Tarihi" />
        <F {...p} col="kesin_kabul_durumu" label="Kesin Kabul Durumu" />
        <F {...p} col="kesin_teminat_mektubu" label="Kesin Teminat Mektubu" />
      </div>
      <div className="mt-3 text-sm" data-testid="ihale-detail-kontrol-teskilati">
        <span className="text-gray-500">Kontrol Teşkilatı:</span>
        {kontrol.length === 0 ? <span className="text-white ml-2">-</span> : (
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {kontrol.map((ad, idx) => <Badge key={idx} className="bg-gray-800 text-gray-200 border-gray-700">{ad}</Badge>)}
          </div>
        )}
      </div>
      {ihale.aciklama && (
        <div className="mt-3" data-testid="ihale-detail-aciklama">
          <span className="text-gray-500 text-sm">Açıklama:</span>
          <p className="text-gray-300 text-sm whitespace-pre-wrap mt-1">{ihale.aciklama}</p>
        </div>
      )}
    </Section>
  );
}
